import React from 'react';

/**
 * SEO component using direct DOM manipulation via useEffect.
 * Why not React 19 metadata hoisting? Because:
 *   - <title> from public/index.html is NOT replaced by React 19 hoist —
 *     it adds a new <title>, leaving the original in the DOM.
 *   - Browsers may pick the first <title>, leading to inconsistent behavior.
 *
 * Strategy:
 *   - Title and meta tags: update via useEffect (idempotent — find or create the element).
 *   - JSON-LD scripts: render via JSX (multiple <script type="application/ld+json"> are OK).
 *   - <html lang>: update via useEffect.
 */

function setMetaTag(attr, attrValue, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${attrValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLinkTag(rel, href, hrefLang = null) {
  if (!href) return;
  const selector = hrefLang
    ? `link[rel="${rel}"][hreflang="${hrefLang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hrefLang) el.setAttribute('hreflang', hrefLang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function SEO({
  title,
  description,
  canonical,
  image = 'https://www.aligcus.com/api/uploads/f823eb4b3952.jpg',
  lang = 'en',
  alternates = [],
  schemas = [],
  noindex = false,
}) {
  const htmlLang = lang === 'zh' ? 'zh-CN' : 'en';
  const ogLocale = lang === 'zh' ? 'zh_CN' : 'en_US';
  const altOgLocale = lang === 'zh' ? 'en_US' : 'zh_CN';
  const validSchemas = (schemas || []).filter(Boolean);
  const xDefaultUrl =
    alternates.find((a) => a.lang === 'en' || a.lang === 'en-US')?.url || canonical;

  React.useEffect(() => {
    if (typeof document === 'undefined') return;

    document.documentElement.lang = htmlLang;

    if (title) document.title = title;

    setMetaTag('name', 'description', description);
    setMetaTag(
      'name',
      'robots',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1'
    );

    if (canonical) setLinkTag('canonical', canonical);

    // Hreflang — overwrite per-language sibling hrefs based on alternates
    alternates.forEach((a) => setLinkTag('alternate', a.url, a.lang));
    if (xDefaultUrl) setLinkTag('alternate', xDefaultUrl, 'x-default');

    // Open Graph
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', canonical);
    setMetaTag('property', 'og:image', image);
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:locale', ogLocale);
    setMetaTag('property', 'og:locale:alternate', altOgLocale);

    // Twitter
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', image);

    // WeChat / 微信
    setMetaTag('itemprop', 'name', title);
    setMetaTag('itemprop', 'description', description);
    setMetaTag('itemprop', 'image', image);
  }, [
    title,
    description,
    canonical,
    image,
    htmlLang,
    ogLocale,
    altOgLocale,
    noindex,
    JSON.stringify(alternates),
    xDefaultUrl,
  ]);

  // JSON-LD schemas — render via JSX. Use a stable id so HMR / re-render doesn't
  // duplicate them across navigations within the SPA.
  return (
    <>
      {validSchemas.map((schema, idx) => (
        <script
          key={`ld-${idx}`}
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}
